/**
 * App plugins: connected apps (e.g. Photoshop) register intents and actions.
 * When the user has an app connected and their message matches a plugin's intent,
 * the chatbot runs the plugin's action (e.g. open image editor) instead of generic flow.
 *
 * Plugin shape:
 * - id: string (e.g. 'photoshop')
 * - name: string (display name)
 * - appId: string (must be in merged connected list to run)
 * - matchIntent: (message: string, connectedApps: string[]) => boolean
 * - action: string (e.g. 'open_image_editor'). The chatbot executes known actions.
 * - editorTarget?: 'image' | 'photoshop' — 'photoshop' opens the dedicated Photoshop workspace (not the generic Image Editor).
 * - getBotMessage?: (options: { hadAttachment?: boolean }) => string
 *
 * To add a new plugin: push to APP_PLUGINS and implement the action in chatbot.js
 * (search for "plugin.action === 'open_image_editor'" to add new cases).
 *
 * MCP tool packs (runtime tools for chat — secrets stay on the backend):
 * - Keep MCP_APP_IDS in sync with backend/src/services/mcp/registry.js
 * - Adding an app via Apps "+" puts its id in localStorage `zolaid-connected-apps`;
 *   chat sends that list + gmailState so the backend can load that app's MCP tools.
 * - To add the next MCP server: see the HOW TO ADD comment in backend mcp/registry.js,
 *   then append the appId here.
 */

/** App ids with a backend MCP tool pack (phase 1). */
export const MCP_APP_IDS = ["gmail", "google-calendar"];

export function hasMcpToolPack(appId) {
  const id = String(appId || "").toLowerCase().trim();
  return MCP_APP_IDS.includes(id);
}

export function mcpAppsFromConnected(connectedApps) {
  const list = Array.isArray(connectedApps) ? connectedApps : [];
  return list.map((id) => String(id || "").toLowerCase()).filter((id) => hasMcpToolPack(id));
}

const PHOTOSHOP_INTENT_PATTERNS = [
  /\b(edit|open|use|launch|start|show)\s+(?:my\s+)?(?:an?\s+)?(image|photo|picture|pic)\s+(?:with\s+)?(?:in\s+)?photoshop\b/i,
  /\bphotoshop\s+(?:to\s+)?(?:edit|open|use)\s+(?:my\s+)?(?:an?\s+)?(image|photo|picture)?/i,
  /\b(edit|open|use)\s+(?:this\s+)?(?:with\s+)?photoshop\b/i,
  /\b(edit|open)\s+(?:in\s+)?photoshop\b/i,
  /\b(edit|open)\s+(?:with\s+)?photoshop\b/i,
  /\b(edit|open)\s+.*photoshop\b/i,
];

function matchPhotoshopIntent(message, connectedApps) {
  if (!Array.isArray(connectedApps) || !connectedApps.includes('photoshop')) return false;
  const lower = String(message || '').toLowerCase();
  return PHOTOSHOP_INTENT_PATTERNS.some((re) => re.test(lower));
}

export const APP_PLUGINS = [
  {
    id: 'photoshop',
    name: 'Photoshop',
    appId: 'photoshop',
    matchIntent: matchPhotoshopIntent,
    action: 'open_image_editor',
    editorTarget: 'photoshop',
    getBotMessage: ({ hadAttachment }) =>
      '**Photoshop** workspace is open above the chat (separate from the generic Image Editor). Use **Adobe Firefly (Photoshop)** in the AI model menu when your server has Adobe credentials; otherwise DALL·E or Nano Banana. ' +
      (hadAttachment ? 'Upload your image in the panel (or drag in) to start.' : 'Upload an image in the Photoshop panel to start.'),
  },
  {
    id: 'canva',
    name: 'Canva',
    appId: 'canva',
    matchIntent(msg, connectedApps) {
      if (!Array.isArray(connectedApps) || !connectedApps.includes('canva')) return false;
      const lower = String(msg || '').toLowerCase();
      return /\b(design|create|make|open|use)\s+(?:a\s+)?(?:post|graphic|banner|flyer|story|with\s+)?canva\b/i.test(lower)
        || /\bcanva\s+(?:to\s+)?(?:design|create|make)\b/i.test(lower)
        || /\b(design|create)\s+.*\bcanva\b/i.test(lower);
    },
    action: 'open_image_editor',
    getBotMessage: () =>
      '**Canva** (plugin) — Image Editor is open above. Create graphics, crop, add text and layers. For full Canva templates, open canva.com in your browser.',
  },
  {
    id: 'spotify',
    name: 'Spotify',
    appId: 'spotify',
    matchIntent(msg, connectedApps) {
      if (!Array.isArray(connectedApps) || !connectedApps.includes('spotify')) return false;
      const lower = String(msg || '').toLowerCase();
      return /\b(play|listen|put on|queue)\s+(?:some\s+)?(?:music|song|track|playlist)\b/i.test(lower)
        || /\b(play|listen)\s+.*\bspotify\b/i.test(lower)
        || /\bspotify\s+(?:play|queue|add)\b/i.test(lower)
        || /\b(create|make)\s+(?:a\s+)?playlist\b/i.test(lower)
        || /\b(what('s| is)\s+)?(?:playing|on)\s+spotify\b/i.test(lower);
    },
    action: 'reply_only',
    getBotMessage: () =>
      '**Spotify** (plugin) — You have Spotify connected. I can suggest playlists, search for tracks, or help you with music. Describe what you want to hear or ask to create a playlist.',
  },
];

/**
 * Merge localStorage "connected" apps with server-side APP_*_API_KEY flags (e.g. photoshop when APP_PHOTOSHOP_API_KEY is set).
 */
export function connectedAppsWithIntegrationKeys(connectedApps, integrationKeyApps = {}) {
  const base = Array.isArray(connectedApps) ? [...connectedApps] : [];
  const seen = new Set(base.map((s) => String(s || '').toLowerCase()));
  for (const [id, on] of Object.entries(integrationKeyApps || {})) {
    if (!on) continue;
    const k = String(id || '').toLowerCase();
    if (k && !seen.has(k)) {
      seen.add(k);
      base.push(k);
    }
  }
  return base;
}

/**
 * Find the first plugin that matches the message and has its app connected (or enabled via server API key).
 */
export function findMatchingPlugin(message, connectedApps, integrationKeyApps = {}) {
  const merged = connectedAppsWithIntegrationKeys(connectedApps, integrationKeyApps);
  if (!merged.length) return null;
  const msg = String(message || '').trim();
  if (!msg) return null;
  return APP_PLUGINS.find((p) => merged.includes(p.appId) && p.matchIntent(msg, merged)) || null;
}
